import { GraphQLError } from 'graphql';
import { camelCase } from 'lodash-es';
import { ReactNode, RefObject, useRef } from 'react';
import { GraphQLTaggedNode, useMutation, UseMutationConfig } from 'react-relay';
import { PayloadError } from 'relay-runtime';

import { useDialogContext } from '../../../contexts/DialogContext';
import { parseGraphQLError } from '../../../utils/error';
import { isFunction } from '../../../utils/is';
import { scrollIntoView, scrollToTop } from '../../../utils/scroll';
import Formik, { FormikConfig, FormikProps, FormikHelpers } from '../Formik';

const CONFIRM_TEXT = '작성 중인 내용이 있어요. 정말 나가실 건가요?';

type Props<
  TMutation extends {
    readonly response: {};
    readonly variables: { readonly input: {}; connections?: ReadonlyArray<string> };
    readonly rawResponse?: {} | undefined;
  },
  TExtraInput extends {} = {},
> = {
  mutation: GraphQLTaggedNode;
  connections?: TMutation['variables']['connections'];
  config?: Omit<UseMutationConfig<TMutation>, 'variables' | 'onCompleted'> & {
    onCompleted?:
      | ((
          response: TMutation['response'],
          errors: PayloadError[] | null,
          formikHelpers: FormikHelpers<TMutation['variables']['input'] & TExtraInput>,
        ) => void | null)
      | undefined;
  };
  onSubmit?: FormikConfig<TMutation['variables']['input'] & TExtraInput>['onSubmit'];
  /**
   * Form에서 다루는 데이터를 변형해서 제출해야하는 경우 사용합니다.
   */
  valuesTransformer?: (values: TMutation['variables']['input'] & TExtraInput) => TMutation['variables']['input'];
  children?:
    | ReactNode
    | ((
        props: FormikProps<TMutation['variables']['input'] & TExtraInput>,
        {
          isMutationInFlight,
          nonFieldErrorRef,
          scrollContainerRef,
        }: {
          isMutationInFlight: boolean;
          nonFieldErrorRef: RefObject<HTMLDivElement>;
          scrollContainerRef: RefObject<HTMLDivElement>;
        },
      ) => ReactNode);
} & Omit<FormikConfig<Partial<TMutation['variables']['input']> & TExtraInput>, 'onSubmit' | 'children'>;

const MutationFormik = <
  TMutation extends {
    readonly response: {};
    readonly variables: { readonly input: {}; connections?: ReadonlyArray<string> };
    readonly rawResponse?: {} | undefined;
  },
  TExtraInput extends {} = {},
>({
  mutation,
  connections,
  config,
  onSubmit,
  children,
  valuesTransformer = (values) => values,
  ...props
}: Props<TMutation, TExtraInput>) => {
  const [mutate, isMutationInFlight] = useMutation(mutation);
  const dialogContext = useDialogContext();

  dialogContext?.setConfirmText(CONFIRM_TEXT);

  const nonFieldErrorRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleSubmit = (
    values: TMutation['variables']['input'] & TExtraInput,
    formikHelpers: FormikHelpers<TMutation['variables']['input'] & TExtraInput>,
  ) => {
    const { setFieldError, setSubmitting } = formikHelpers;

    setSubmitting(true);

    onSubmit?.(values, formikHelpers);
    mutate({
      ...config,
      onError: (e) => {
        config?.onError?.(e);

        const errors = parseGraphQLError(e as GraphQLError);
        for (const error of errors || []) {
          const { field: _field, message } = error;
          const field = camelCase(_field || '') || 'nonFieldError';
          setFieldError(field, message);

          if (field === 'nonFieldError')
            setTimeout(() => {
              if (nonFieldErrorRef.current && scrollContainerRef.current)
                scrollIntoView(nonFieldErrorRef.current, scrollContainerRef.current, { behavior: 'smooth' });
            }, 0);
          else {
            if (scrollContainerRef.current) scrollToTop(scrollContainerRef.current, { behavior: 'smooth' });
          }
        }

        setSubmitting(false);
      },
      onCompleted: (response, errors) => {
        config?.onCompleted?.(response, errors, formikHelpers);

        setSubmitting(false);
      },
      variables: { connections: connections || [], input: { ...valuesTransformer(values) } },
    });
  };

  return (
    <Formik<TMutation['variables']['input'] & TExtraInput> onSubmit={handleSubmit} enableReinitialize {...props}>
      {({ dirty, ...formikProps }) => {
        dialogContext?.setIsConfirm(dirty);
        return isFunction(children)
          ? children({ dirty, ...formikProps }, { isMutationInFlight, nonFieldErrorRef, scrollContainerRef })
          : children;
      }}
    </Formik>
  );
};

export default MutationFormik;
export type { Props as MutationFormikProps };
